import { HttpClient } from "@angular/common/http";
import { DestroyRef, inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { MsalService } from "@azure/msal-angular";
import { catchError, defer, finalize, first, firstValueFrom, map, Observable, of, Subject, tap, throwError } from "rxjs";
import { Client, UserManagementResponseModel, UserResponseModel } from "src/app/api/client";
import { environment } from "src/environments/environment";
import { e2eTokenStorageKey } from "../consts/storage-keys";
import { EnvironmentId } from "../enums/environment-id.enum";

@Injectable({
  providedIn: "root",
})
export class UserInfoService {
  protected readonly http = inject(HttpClient);
  protected readonly client = inject(Client);
  protected readonly msal = inject(MsalService);
  protected readonly router = inject(Router);
  protected readonly destroyRef = inject(DestroyRef);

  private readonly currentUserMap = new Map<string, UserResponseModel>();
  private readonly currentUserPendingRequests = new Map<string, Subject<UserResponseModel>>();

  private readonly userRolesMap = new Map<string, UserManagementResponseModel>();
  private readonly userRolesPendingRequests = new Map<string, Subject<UserManagementResponseModel>>();

  public getUserId(): string | null {
    if (environment.id !== EnvironmentId.Production && sessionStorage.getItem(e2eTokenStorageKey)) {
      return sessionStorage.getItem(e2eTokenStorageKey);
    }
    return this.msal.instance.getActiveAccount()?.username || null;
  }

  public getCurrentUser(): Observable<UserResponseModel | null> {
    return defer(() => {
      const userId = this.getUserId();
      if (!userId) {
        return of(null);
      }
      if (this.currentUserMap.has(userId)) {
        return of(this.cloneResponse(this.currentUserMap.get(userId) ?? null));
      }
      if (this.currentUserPendingRequests.has(userId)) {
        const savedPending$ = this.currentUserPendingRequests.get(userId);
        if (savedPending$) return savedPending$.pipe(map((res) => this.cloneResponse(res)));
      }
      const pending$ = new Subject<UserResponseModel>();
      this.currentUserPendingRequests.set(userId, pending$);
      return this.client.profile().pipe(
        first(),
        map((res) => res.data),
        tap((res) => {
          this.currentUserMap.set(userId, res);
          pending$.next(res);
        }),
        finalize(() => {
          if (!pending$.closed) pending$.complete();
          this.currentUserPendingRequests.delete(userId);
        }),
        map((res) => this.cloneResponse(res)),
        catchError((err: unknown) => {
          pending$.error(err);
          return throwError(() => err);
        })
      );
    });
  }

  public getMyUserInfo(): Observable<UserManagementResponseModel | null> {
    return defer(() => {
      const userId = this.getUserId();
      if (!userId) {
        return of(null);
      }
      if (this.userRolesMap.has(userId)) {
        return of(this.cloneResponse(this.userRolesMap.get(userId) ?? null));
      }
      if (this.userRolesPendingRequests.has(userId)) {
        const savedPending$ = this.userRolesPendingRequests.get(userId);
        if (savedPending$) return savedPending$.pipe(map((res) => this.cloneResponse(res)));
      }
      const pending$ = new Subject<UserManagementResponseModel>();
      this.userRolesPendingRequests.set(userId, pending$);
      return this.client.getMyUserInfo().pipe(
        first(),
        map((res) => res.data),
        tap((res) => {
          this.userRolesMap.set(userId, res);
          pending$.next(res);
        }),
        finalize(() => {
          if (!pending$.closed) pending$.complete();
          this.userRolesPendingRequests.delete(userId);
        }),
        map((res) => this.cloneResponse(res)),
        catchError((err: unknown) => {
          pending$.error(err);
          return throwError(() => err);
        })
      );
    });
  }

  public clearMyUser(): boolean {
    const userId = this.getUserId();
    if (userId) {
      this.currentUserPendingRequests.get(userId)?.complete();
      this.userRolesPendingRequests.get(userId)?.complete();
      this.currentUserPendingRequests.delete(userId);
      this.userRolesPendingRequests.delete(userId);
      return [this.currentUserMap.delete(userId), this.userRolesMap.delete(userId)].every((res) => res);
    }
    return false;
  }

  public async logout(): Promise<void> {
    this.clearMyUser();
    if (environment.id !== EnvironmentId.Production && sessionStorage.getItem(e2eTokenStorageKey)) {
      sessionStorage.removeItem(e2eTokenStorageKey);
      await this.router.navigate(["/"]);
      window.location.reload();
    } else {
      const activeAccount = this.msal.instance.getActiveAccount();
      await firstValueFrom(
        this.msal.logoutRedirect({
          account: activeAccount ?? null,
          logoutHint: activeAccount?.idTokenClaims?.login_hint || undefined,
        })
      );
    }
  }

  private cloneResponse<T>(value: T): T {
    return typeof window.structuredClone === "function" ? structuredClone(value) : JSON.parse(JSON.stringify(value));
  }
}
